import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';
import {
  UserIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  CalendarIcon,
  IdentificationIcon,
  DocumentTextIcon,
  PhotoIcon,
  CogIcon,
  CloudIcon,
  CreditCardIcon,
  PhoneIcon,
  InformationCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import SubscriptionDetails from '../components/SubscriptionDetails';
import UserTokenUsageHistory from '../components/SRO/UserTokenUsageHistory';

const Profile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [ntnCnicType, setNtnCnicType] = useState('ntn'); // 'ntn' or 'cnic'
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [apiEnvironment, setApiEnvironment] = useState('sandbox'); // 'sandbox' or 'production'
  const [selectedBusinessActivities, setSelectedBusinessActivities] = useState([]);

  // Profile form
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    setValue: setProfileValue
  } = useForm();

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch: watchPassword
  } = useForm();

  // Load provinces on component mount
  useEffect(() => {
    const loadProvinces = async () => {
      try {
        // We'll use hardcoded provinces for now
        // const response = await apiClient.get('/reference/registration-types?limit=100');
        const provinceOptions = [
          { code: 'PB', name: 'Punjab' },
          { code: 'SD', name: 'Sindh' },
          { code: 'KP', name: 'Khyber Pakhtunkhwa' },
          { code: 'BL', name: 'Balochistan' },
          { code: 'GB', name: 'Gilgit-Baltistan' },
          { code: 'AK', name: 'Azad Kashmir' },
          { code: 'ICT', name: 'Islamabad Capital Territory' },
          { code: 'FATA', name: 'Federally Administered Tribal Areas' }
        ];
        setProvinces(provinceOptions);
      } catch (error) {
        console.error('Failed to load provinces:', error);
        // Fallback to hardcoded provinces
        setProvinces([
          { code: 'PB', name: 'Punjab' },
          { code: 'SD', name: 'Sindh' },
          { code: 'KP', name: 'Khyber Pakhtunkhwa' },
          { code: 'BL', name: 'Balochistan' },
          { code: 'GB', name: 'Gilgit-Baltistan' },
          { code: 'AK', name: 'Azad Kashmir' },
          { code: 'ICT', name: 'Islamabad Capital Territory' },
          { code: 'FATA', name: 'Federally Administered Tribal Areas' }
        ]);
      }
    };

    loadProvinces();
  }, []);

  // Set initial form values when user data is available
  useEffect(() => {
    if (user) {
      // Handle NTN/CNIC field - determine type and set value
      if (user.ntn_cnic && typeof user.ntn_cnic === 'string') {
        const cleaned = user.ntn_cnic.replace(/[\s-]/g, '');
        if (cleaned.length === 7) {
          setNtnCnicType('ntn');
          setProfileValue('ntn_cnic', user.ntn_cnic);
        } else if (cleaned.length === 13) {
          setNtnCnicType('cnic');
          setProfileValue('ntn_cnic', user.ntn_cnic);
        } else {
          // Default to NTN if unclear
          setNtnCnicType('ntn');
          setProfileValue('ntn_cnic', user.ntn_cnic);
        }
      } else {
        // If ntn_cnic is null or not a string, default to NTN type with empty value
        setNtnCnicType('ntn');
        setProfileValue('ntn_cnic', '');
      }

      setProfileValue('business_name', user.business_name || '');
      setProfileValue('province', user.province || '');
      setProfileValue('address', user.address || '');
      setProfileValue('mobile_number', user.mobile_number || '');
      setProfileValue('business_activity', user.business_activity || '');

      // Handle multiple business activities
      const userActivities = user.business_activities || (user.business_activity ? [user.business_activity] : []);
      setSelectedBusinessActivities(userActivities);
      setProfileValue('business_activities', userActivities);

      setProfileValue('sector', user.sector || '');

      // Set FBR API configuration values (only tokens, URLs are static)
      // Don't set masked tokens in the form - leave them empty if masked
      const sandboxToken = user.sandbox_token;
      const productionToken = user.production_token;
      setProfileValue('sandbox_token', (sandboxToken && sandboxToken.includes('***')) ? '' : (sandboxToken || ''));
      setProfileValue('production_token', (productionToken && productionToken.includes('***')) ? '' : (productionToken || ''));
      setApiEnvironment(user.api_environment || 'sandbox');
      setProfileValue('api_environment', user.api_environment || 'sandbox');
    }
  }, [user, setProfileValue]);

  // Handle business activity selection changes
  const handleBusinessActivityChange = (activity) => {
    setSelectedBusinessActivities(prev => {
      if (prev.includes(activity)) {
        // Remove activity if already selected
        return prev.filter(a => a !== activity);
      } else {
        // Add activity if not selected
        return [...prev, activity];
      }
    });
  };

  const onProfileSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Include selected business activities in the submission
      const submissionData = {
        ...data,
        business_activities: selectedBusinessActivities
      };
      console.log('🚀 FRONTEND: Profile form data being submitted:', JSON.stringify(submissionData, null, 2));
      const response = await updateProfile(submissionData);
      console.log('✅ FRONTEND: Profile update response:', JSON.stringify(response, null, 2));
      // Success message is already shown by AuthContext
    } catch (error) {
      console.error('❌ FRONTEND: Profile update error:', error);
      console.error('❌ FRONTEND: Error response data:', error.response?.data);
      console.error('❌ FRONTEND: Error response status:', error.response?.status);
      console.error('❌ FRONTEND: Full error object:', JSON.stringify(error, null, 2));
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword
      });
      if (result.success) {
        resetPassword();
        toast.success('Password changed successfully!');
      }
    } catch (error) {
      console.error('Password change error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await apiClient.post('/auth/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        toast.success('Logo uploaded successfully!');
        // Update user context with new logo
        await updateProfile({ logo: response.data.logoUrl });
        setLogoFile(null);
        setLogoPreview(null);
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload logo');
    } finally {
      setIsLoading(false);
    }
  };

  const removeLogo = async () => {
    setIsLoading(true);
    try {
      await updateProfile({ logo: null });
      toast.success('Logo removed successfully!');
      setLogoPreview(null);
      setLogoFile(null);
    } catch (error) {
      console.error('Logo removal error:', error);
      toast.error('Failed to remove logo');
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile Information', icon: UserIcon },
    { id: 'subscription', name: 'Subscription Details', icon: CreditCardIcon },
    { id: 'token-usage', name: 'Token Usage', icon: SparklesIcon },
    { id: 'logo', name: 'Company Logo', icon: PhotoIcon },
    { id: 'fbr-api', name: 'FBR API Settings', icon: CloudIcon },
    { id: 'password', name: 'Change Password', icon: LockClosedIcon }
  ];

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-4">
          <div className="h-16 w-16 rounded-full bg-blue-500 flex items-center justify-center">
            <span className="text-2xl font-bold text-white">
              {user.business_name?.charAt(0) || 'U'}
            </span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.business_name}</h1>
            <p className="text-gray-600">{user.email}</p>
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <CalendarIcon className="h-4 w-4 mr-1" />
              Member since {formatDate(user.created_at)}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Business Information</h3>
                <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
                  {/* NTN/CNIC Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <IdentificationIcon className="h-4 w-4 inline mr-1" />
                      Tax Identification Type
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="ntn"
                          checked={ntnCnicType === 'ntn'}
                          onChange={(e) => setNtnCnicType(e.target.value)}
                          className="mr-2 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">NTN (National Tax Number)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="cnic"
                          checked={ntnCnicType === 'cnic'}
                          onChange={(e) => setNtnCnicType(e.target.value)}
                          className="mr-2 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">CNIC (Computerized National Identity Card)</span>
                      </label>
                    </div>
                  </div>

                  {/* NTN/CNIC Number */}
                  <div>
                    <label htmlFor="ntn_cnic" className="block text-sm font-medium text-gray-700 mb-2">
                      <DocumentTextIcon className="h-4 w-4 inline mr-1" />
                      {ntnCnicType === 'ntn' ? 'NTN Number' : 'CNIC Number'}
                    </label>
                    <input
                      type="text"
                      id="ntn_cnic"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        profileErrors.ntn_cnic ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder={ntnCnicType === 'ntn' ? 'Enter your 7-digit NTN number' : 'Enter your 13-digit CNIC number'}
                      {...registerProfile('ntn_cnic', {
                        required: `${ntnCnicType === 'ntn' ? 'NTN' : 'CNIC'} number is required`,
                        pattern: {
                          value: ntnCnicType === 'ntn'
                            ? /^\d{7}$/
                            : /^\d{5}-?\d{7}-?\d{1}$/,
                          message: ntnCnicType === 'ntn'
                            ? 'NTN must be exactly 7 digits'
                            : 'CNIC must be in format XXXXX-XXXXXXX-X or 13 digits'
                        },
                        validate: (value) => {
                          if (!value) return true; // Let required handle empty values
                          const cleaned = value.replace(/[\s-]/g, '');
                          if (ntnCnicType === 'ntn') {
                            return cleaned.length === 7 || 'NTN must be exactly 7 digits';
                          } else {
                            return cleaned.length === 13 || 'CNIC must be exactly 13 digits';
                          }
                        }
                      })}
                    />
                    {profileErrors.ntn_cnic && (
                      <p className="mt-1 text-sm text-red-600">{profileErrors.ntn_cnic.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      {ntnCnicType === 'ntn'
                        ? 'Enter your 7-digit National Tax Number'
                        : 'Enter your 13-digit CNIC (with or without dashes)'}
                    </p>
                  </div>

                  {/* Business Name */}
                  <div>
                    <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-2">
                      <BuildingOfficeIcon className="h-4 w-4 inline mr-1" />
                      Business Name
                    </label>
                    <input
                      type="text"
                      id="business_name"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        profileErrors.business_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter your business name"
                      {...registerProfile('business_name', {
                        required: 'Business name is required',
                        minLength: {
                          value: 2,
                          message: 'Business name must be at least 2 characters'
                        },
                        maxLength: {
                          value: 255,
                          message: 'Business name must not exceed 255 characters'
                        }
                      })}
                    />
                    {profileErrors.business_name && (
                      <p className="mt-1 text-sm text-red-600">{profileErrors.business_name.message}</p>
                    )}
                  </div>

                  {/* Province */}
                  <div>
                    <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPinIcon className="h-4 w-4 inline mr-1" />
                      Province
                    </label>
                    <select
                      id="province"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        profileErrors.province ? 'border-red-300' : 'border-gray-300'
                      }`}
                      {...registerProfile('province', {
                        required: 'Province is required'
                      })}
                    >
                      <option value="">Select a province</option>
                      {provinces.map((province) => (
                        <option key={province.code} value={province.name}>
                          {province.name}
                        </option>
                      ))}
                    </select>
                    {profileErrors.province && (
                      <p className="mt-1 text-sm text-red-600">{profileErrors.province.message}</p>
                    )}
                  </div>

                  {/* Address */}
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPinIcon className="h-4 w-4 inline mr-1" />
                      Address
                    </label>
                    <textarea
                      id="address"
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        profileErrors.address ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter your complete business address"
                      {...registerProfile('address', {
                        required: 'Address is required',
                        minLength: {
                          value: 10,
                          message: 'Address must be at least 10 characters'
                        },
                        maxLength: {
                          value: 500,
                          message: 'Address must not exceed 500 characters'
                        }
                      })}
                    />
                    {profileErrors.address && (
                      <p className="mt-1 text-sm text-red-600">{profileErrors.address.message}</p>
                    )}
                  </div>

                  {/* Mobile Number */}
                  <div>
                    <label htmlFor="mobile_number" className="block text-sm font-medium text-gray-700 mb-2">
                      <PhoneIcon className="h-4 w-4 inline mr-1" />
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      id="mobile_number"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        profileErrors.mobile_number ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter your mobile number (e.g., 03001234567)"
                      {...registerProfile('mobile_number', {
                        required: 'Mobile number is required',
                        pattern: {
                          value: /^(\+92|0)?[0-9]{10,11}$/,
                          message: 'Please enter a valid Pakistani mobile number'
                        }
                      })}
                    />
                    {profileErrors.mobile_number && (
                      <p className="mt-1 text-sm text-red-600">{profileErrors.mobile_number.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Enter your mobile number (e.g., 03001234567 or +923001234567)
                    </p>
                  </div>

                  {/* Business Activities (Multiple Selection) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <BuildingOfficeIcon className="h-4 w-4 inline mr-1" />
                      Business Activities <span className="text-red-500">*</span>
                    </label>
                    <div className="border rounded-md p-3 bg-gray-50 max-h-48 overflow-y-auto">
                      {['Manufacturer', 'Importer', 'Distributor', 'Wholesaler', 'Exporter', 'Retailer', 'Service Provider', 'Other'].map((activity) => (
                        <label key={activity} className="flex items-center space-x-2 mb-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={selectedBusinessActivities.includes(activity)}
                            onChange={() => handleBusinessActivityChange(activity)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">{activity}</span>
                        </label>
                      ))}
                    </div>
                    {selectedBusinessActivities.length === 0 && (
                      <p className="mt-1 text-sm text-red-600">At least one business activity is required</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Select all business activities that apply to your business as per FBR requirements
                    </p>
                    {selectedBusinessActivities.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600">Selected: {selectedBusinessActivities.join(', ')}</p>
                      </div>
                    )}
                  </div>

                  {/* Business Sector */}
                  <div>
                    <label htmlFor="sector" className="block text-sm font-medium text-gray-700 mb-2">
                      <CogIcon className="h-4 w-4 inline mr-1" />
                      Business Sector <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="sector"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        profileErrors.sector ? 'border-red-300' : 'border-gray-300'
                      }`}
                      {...registerProfile('sector', {
                        required: 'Business Sector is required'
                      })}
                    >
                      <option value="">Select Business Sector</option>
                      <option value="All Other Sectors">All Other Sectors</option>
                      <option value="Steel">Steel</option>
                      <option value="FMCG">FMCG</option>
                      <option value="Textile">Textile</option>
                      <option value="Telecom">Telecom</option>
                      <option value="Petroleum">Petroleum</option>
                      <option value="Electricity Distribution">Electricity Distribution</option>
                      <option value="Gas Distribution">Gas Distribution</option>
                      <option value="Services">Services</option>
                      <option value="Automobile">Automobile</option>
                      <option value="CNG Stations">CNG Stations</option>
                      <option value="Pharmaceuticals">Pharmaceuticals</option>
                      <option value="Wholesale / Retails">Wholesale / Retails</option>
                      <option value="Potassium Chlorate">Potassium Chlorate</option>
                      <option value="Cement or Concrete Blocks">Cement or Concrete Blocks</option>
                      <option value="Mobile">Mobile</option>
                    </select>
                    {profileErrors.sector && (
                      <p className="mt-1 text-sm text-red-600">{profileErrors.sector.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Select your business sector as per FBR classification
                    </p>
                  </div>

                  {/* Email (Read-only) */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={user.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                    <p className="mt-1 text-sm text-gray-500">Email address cannot be changed</p>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Updating...</span>
                        </>
                      ) : (
                        <span>Update Profile</span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'logo' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Company Logo</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <PhotoIcon className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">Logo Requirements</h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Image formats: JPG, PNG, GIF</li>
                          <li>Maximum file size: 2MB</li>
                          <li>Recommended dimensions: 200x200 pixels</li>
                          <li>Square images work best</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Current Logo */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Current Logo</h4>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      {user?.logo ? (
                        <div className="space-y-4">
                          <img
                            src={user.logo && user.logo.startsWith('/uploads/') ? `https://fbrstapp.com${user.logo}` : user.logo}
                            alt="Company Logo"
                            className="mx-auto h-24 w-24 object-contain rounded-lg border border-gray-200"
                          />
                          <div>
                            <button
                              onClick={removeLogo}
                              disabled={isLoading}
                              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isLoading ? 'Removing...' : 'Remove Logo'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <p className="text-gray-500">No logo uploaded</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Upload New Logo */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Upload New Logo</h4>
                    <div className="space-y-4">
                      <div>
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="logo-upload"
                          className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <PhotoIcon className="h-4 w-4 mr-2" />
                          Choose File
                        </label>
                      </div>

                      {logoPreview && (
                        <div className="space-y-4">
                          <div className="border border-gray-300 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                            <img
                              src={logoPreview}
                              alt="Logo Preview"
                              className="h-24 w-24 object-contain rounded-lg border border-gray-200"
                            />
                          </div>
                          <div className="flex space-x-3">
                            <button
                              onClick={uploadLogo}
                              disabled={isLoading}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isLoading ? 'Uploading...' : 'Upload Logo'}
                            </button>
                            <button
                              onClick={() => {
                                setLogoFile(null);
                                setLogoPreview(null);
                              }}
                              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'fbr-api' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">FBR API Configuration</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CloudIcon className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">FBR API Settings</h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>Configure your FBR Digital Invoicing API credentials for both SandBox (testing) and Production environments.</p>
                        <p className="mt-1">These settings are encrypted and stored securely.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
                  {/* API Environment Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Active Environment
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="sandbox"
                          checked={apiEnvironment === 'sandbox'}
                          onChange={(e) => {
                            setApiEnvironment(e.target.value);
                            setProfileValue('api_environment', e.target.value);
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">SandBox (Testing)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="production"
                          checked={apiEnvironment === 'production'}
                          onChange={(e) => {
                            setApiEnvironment(e.target.value);
                            setProfileValue('api_environment', e.target.value);
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Production (Live)</span>
                      </label>
                    </div>
                  </div>

                  {/* SandBox Configuration */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      <CogIcon className="h-5 w-5 mr-2 text-orange-500" />
                      SandBox Environment (Testing)
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      {/* Static URL Information */}
                      <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-4">
                        <div className="flex items-center">
                          <InformationCircleIcon className="h-5 w-5 text-orange-400 mr-2" />
                          <h5 className="text-sm font-medium text-orange-800">FBR API Endpoint</h5>
                        </div>
                        <p className="text-sm text-orange-700 mt-1">
                          <strong>API URL:</strong> https://gw.fbr.gov.pk/di_data/v1/di
                        </p>
                        <p className="text-xs text-orange-600 mt-1">
                          Sandbox/Production routing is determined by your security token.
                        </p>
                      </div>
                      <div>
                        <label htmlFor="sandbox_token" className="block text-sm font-medium text-gray-700 mb-2">
                          SandBox Security Token
                        </label>
                        <input
                          type="password"
                          id="sandbox_token"
                          {...registerProfile('sandbox_token')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder={user?.sandbox_token && user.sandbox_token.includes('***') ? 'Token saved (leave empty to keep existing)' : '6e35d02d-01fc-355d-acec-458219f06b61'}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {user?.sandbox_token && user.sandbox_token.includes('***')
                            ? 'Token is saved and encrypted. Leave empty to keep existing token, or enter new token to replace.'
                            : 'Your SandBox security token from FBR'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Production Configuration */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      <CogIcon className="h-5 w-5 mr-2 text-green-500" />
                      Production Environment (Live)
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      {/* Static URL Information */}
                      <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                        <div className="flex items-center">
                          <InformationCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                          <h5 className="text-sm font-medium text-green-800">FBR API Endpoint</h5>
                        </div>
                        <p className="text-sm text-green-700 mt-1">
                          <strong>API URL:</strong> https://gw.fbr.gov.pk/di_data/v1/di
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Sandbox/Production routing is determined by your security token.
                        </p>
                      </div>
                      <div>
                        <label htmlFor="production_token" className="block text-sm font-medium text-gray-700 mb-2">
                          Production Security Token
                        </label>
                        <input
                          type="password"
                          id="production_token"
                          {...registerProfile('production_token')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder={user?.production_token && user.production_token.includes('***') ? 'Token saved (leave empty to keep existing)' : 'Your production security token'}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {user?.production_token && user.production_token.includes('***')
                            ? 'Token is saved and encrypted. Leave empty to keep existing token, or enter new token to replace.'
                            : 'Your Production security token from FBR'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <CloudIcon className="h-4 w-4" />
                          <span>Save FBR API Settings</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Management</h3>
                <SubscriptionDetails />
              </div>
            </div>
          )}

          {activeTab === 'token-usage' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">LLM Token Usage & Analytics</h3>
                <UserTokenUsageHistory userId={user?.id} />
              </div>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <LockClosedIcon className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Password Requirements</h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>At least 8 characters long</li>
                          <li>Contains at least one uppercase letter</li>
                          <li>Contains at least one lowercase letter</li>
                          <li>Contains at least one number</li>
                          <li>Contains at least one special character (@$!%*?&)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6">
                  {/* Current Password */}
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        id="currentPassword"
                        className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          passwordErrors.currentPassword ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Enter your current password"
                        {...registerPassword('currentPassword', {
                          required: 'Current password is required'
                        })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.currentPassword && (
                      <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                    )}
                  </div>

                  {/* New Password */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="newPassword"
                        className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          passwordErrors.newPassword ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Enter your new password"
                        {...registerPassword('newPassword', {
                          required: 'New password is required',
                          minLength: {
                            value: 8,
                            message: 'Password must be at least 8 characters long'
                          },
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                            message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
                          }
                        })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          passwordErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Confirm your new password"
                        {...registerPassword('confirmPassword', {
                          required: 'Please confirm your new password',
                          validate: (value) => {
                            const newPassword = watchPassword('newPassword');
                            return value === newPassword || 'Passwords do not match';
                          }
                        })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Changing...</span>
                        </>
                      ) : (
                        <>
                          <LockClosedIcon className="h-4 w-4" />
                          <span>Change Password</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
